import { Link } from 'react-router-dom';
import {
  Link2,
  User,
  BarChart3,
  ArrowRight,
  TrendingUp,
  MousePointerClick,
  Plus,
  Copy,
  Check,
  ExternalLink,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '@/components/layout';
import { Card, CardTitle, Button, Badge, PlatformIcon } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useDeeplinks } from '@/hooks/useDeeplinks';
import { usePage } from '@/hooks/usePage';
import { getDashboardQuickStats } from '@/services/deeplinks';
import type { Platform } from '@/types';

const RecentDeeplinkItem = ({
  name,
  shortUrl,
  platform,
}: {
  name: string;
  shortUrl: string;
  platform: Platform;
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(`https://${shortUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
      <PlatformIcon platform={platform} size="md" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-dark-800 dark:text-white truncate">{name}</p>
        <p className="text-sm text-lime-600 dark:text-lime-400 truncate">{shortUrl}</p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={handleCopy}
          className="p-2 text-gray-400 hover:text-lime-600 hover:bg-lime-50 dark:hover:bg-lime-900/30 rounded-xl transition-colors"
          title={t('common.copyLinkTooltip')}
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
        <a
          href={`https://${shortUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-gray-400 hover:text-dark-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700 rounded-xl transition-colors"
          title={t('common.openLinkTooltip')}
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { deeplinks, loading } = useDeeplinks();
  const { page } = usePage();

  const [quickStats, setQuickStats] = useState<{ totalClicks: number; weekClicks: number } | null>(null);
  const [quickLoading, setQuickLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setQuickLoading(true);
    getDashboardQuickStats(user.uid)
      .then(setQuickStats)
      .catch((err) => console.error('Quick stats error:', err))
      .finally(() => setQuickLoading(false));
  }, [user]);

  const profileUsername = page?.username || user?.username || 'username';

  const totalClicks = quickStats?.totalClicks ?? 0;
  const weekClicks = quickStats?.weekClicks ?? 0;

  const stats = [
    {
      label: t('dashboard.totalClicks'),
      value: quickLoading ? null : totalClicks.toLocaleString(i18n.language),
      change: '',
      trend: 'neutral',
      icon: <MousePointerClick className="w-5 h-5" />,
      color: 'bg-lime-100 text-lime-600',
    },
    {
      label: t('dashboard.activeLinks'),
      value: deeplinks.length.toString(),
      change: '',
      trend: 'neutral',
      icon: <Link2 className="w-5 h-5" />,
      color: 'bg-lavender-100 text-lavender-600',
    },
    {
      label: t('dashboard.thisWeek'),
      value: quickLoading ? null : weekClicks.toLocaleString(i18n.language),
      change: '',
      trend: 'neutral',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'bg-green-100 text-green-600',
    },
  ];

  const recentDeeplinks = deeplinks.slice(0, 5);

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">
          {user?.displayName?.split(' ')[0]
            ? t('dashboard.welcomeBack', { name: user.displayName.split(' ')[0] })
            : t('dashboard.welcomeBackDefault')}
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          {t('dashboard.subtitle')}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-8">
        {stats.map((stat, index) => (
          <Card key={index} variant="default" padding="md">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.color}`}>
                {stat.icon}
              </div>
              {stat.change && (
                <Badge variant={stat.trend === 'up' ? 'lime' : 'lavender'} size="sm">
                  {stat.change}
                </Badge>
              )}
            </div>
            {stat.value === null ? (
              <div className="h-8 w-12 bg-gray-200 dark:bg-dark-600 rounded-lg animate-pulse" />
            ) : (
              <p className="text-2xl font-bold text-dark-800 dark:text-white">{stat.value}</p>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Links */}
        <div className="lg:col-span-2">
          <Card variant="default" padding="none">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-600">
              <CardTitle>{t('dashboard.recentDeeplinks')}</CardTitle>
              <Link to="/deeplinks">
                <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                  {t('dashboard.viewAll')}
                </Button>
              </Link>
            </div>

            {loading ? (
              <div className="p-6 text-center text-gray-500">{t('common.loading')}</div>
            ) : recentDeeplinks.length > 0 ? (
              <div className="divide-y divide-gray-100 dark:divide-dark-600">
                {recentDeeplinks.map((deeplink) => {
                  const shortUrl = `klipr.it/${deeplink.code}`;
                  return (
                    <RecentDeeplinkItem
                      key={deeplink.id}
                      name={deeplink.name || shortUrl}
                      shortUrl={shortUrl}
                      platform={deeplink.platform}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-12 h-12 bg-gray-100 dark:bg-dark-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Link2 className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 mb-4">{t('dashboard.noDeeplinksYet')}</p>
                <Link to="/deeplinks">
                  <Button variant="primary" size="sm" leftIcon={<Plus className="w-4 h-4" />}>
                    {t('dashboard.createFirstLink')}
                  </Button>
                </Link>
              </div>
            )}
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          {/* Quick Actions Card */}
          <Card variant="default" padding="md">
            <CardTitle className="mb-4">{t('dashboard.quickActions')}</CardTitle>
            <div className="space-y-3">
              <Link to="/deeplinks" className="block">
                <button className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors text-left">
                  <div className="w-10 h-10 bg-lime-100 dark:bg-lime-900/30 rounded-xl flex items-center justify-center">
                    <Link2 className="w-5 h-5 text-lime-600 dark:text-lime-400" />
                  </div>
                  <div>
                    <p className="font-medium text-dark-800 dark:text-white">{t('dashboard.createDeeplink')}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard.addNewSmartLink')}</p>
                  </div>
                </button>
              </Link>

              <Link to="/profile" className="block">
                <button className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors text-left">
                  <div className="w-10 h-10 bg-lavender-100 dark:bg-lavender-900/30 rounded-xl flex items-center justify-center">
                    <User className="w-5 h-5 text-lavender-600 dark:text-lavender-400" />
                  </div>
                  <div>
                    <p className="font-medium text-dark-800 dark:text-white">{t('dashboard.editProfile')}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard.customizeYourPage')}</p>
                  </div>
                </button>
              </Link>

              <Link to="/stats" className="block">
                <button className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors text-left">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-dark-800 dark:text-white">{t('dashboard.viewAnalytics')}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard.trackYourGrowth')}</p>
                  </div>
                </button>
              </Link>
            </div>
          </Card>

          {/* Profile Link Card */}
          <Card variant="dark" padding="md">
            <p className="text-white/60 text-sm mb-2">{t('dashboard.yourProfileLink')}</p>
            <p className="text-white font-medium mb-4">
              klipr.it/@{profileUsername}
            </p>
            <Button
              variant="primary"
              size="sm"
              className="w-full"
              onClick={() => {
                navigator.clipboard.writeText(
                  `https://klipr.it/@${profileUsername}`
                );
              }}
            >
              {t('common.copyLink')}
            </Button>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
